"use client";

import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  type SetStateAction,
} from "react";
import { useParams } from "next/navigation";
import { useSession } from "@sajilni/auth";
import { usePubNubNotifications } from "@/hooks/usePubNubNotifications";
import {
  BellRing,
  MessageCircle,
  Users2,
  Building2,
  User,
  Clock,
  Loader2,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { cn } from "@sajilni/utils/cn";
import { useTranslation, useLocale } from "@sajilni/localization";
import {
  baseApi,
  useSWRFetcher,
  useSWRMutationHook,
} from "@sajilni/api-handler";
import {
  Button,
  Badge,
  Skeleton,
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "ui";
import { formatDistanceToNow } from "date-fns";
import { ar } from "date-fns/locale";
import {
  ConnectionStatus,
  NotificationsType,
  MeetingStatus,
  MeetingParticipantType,
  ParticipantType,
  TransactionsAlertFeature,
  TransactionsAlertType,
} from "database";
import moment from "moment-timezone";
import { useSassTrackStore } from "@sajilni/machines/zustand/sassTrack";
import {
  connectionNotificationPeerIds,
  directoryEntryMatchesPeers,
  displayCompanyName,
  displayPersonName,
  emitLiveConnectionStatus,
  isInviteeSideConfirmation,
} from "@/lib/notification-party";
import { mutate as globalMutate } from "swr";

type PartyKind = "user" | "business";

type PersonParty = {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
  profiles?: {
    id?: string | null;
    name?: string | null;
    image?: string | null;
  }[] | null;
};

type CompanyParty = {
  id: string;
  translations?: { name?: string | null }[] | null;
  boothName?: string | null;
  name?: string | null;
  email?: string | null;
  logo?: string | null;
};

type InboxMeeting = {
  id: string;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  participants?: {
    entityId?: string | null;
    type?: MeetingParticipantType | string;
  }[] | null;
};

type TransactionsAlertFields = {
  feature?: TransactionsAlertFeature;
  type?: TransactionsAlertType;
};

export type InboxNotification = {
  id: string;
  type: NotificationsType;
  entityId: string;
  readAt?: Date | string | null;
  sentAt: Date | string;
  sender: ParticipantType;
  receiver: ParticipantType;
  senderId?: string | null;
  receiverId: string;
  senderUser?: PersonParty | null;
  senderExhibitor?: CompanyParty | null;
  senderSponsor?: CompanyParty | null;
  receiverUser?: PersonParty | null;
  receiverExhibitor?: CompanyParty | null;
  receiverSponsor?: CompanyParty | null;
  connectionRequest?: {
    senderId?: string | null;
    receiverId?: string | null;
  } | null;
  meeting?: InboxMeeting | null;
  event?: { timezone?: string | null } | null;
  transactionsAlerts?: TransactionsAlertFields[] | null;
  transactionsAlert?: TransactionsAlertFields | null;
  title?: string | null;
  body?: string | null;
};

type InboxListResponse = {
  response?: {
    notifications?: InboxNotification[];
    pagination?: {
      currentPage?: number;
      total?: number;
      totalUnread?: number;
    };
  };
};

function patchNotificationInCache(
  cached: InboxListResponse | undefined,
  id: string,
  patch: Partial<InboxNotification>
): InboxListResponse | undefined {
  if (!cached?.response) return cached;
  return {
    ...cached,
    response: {
      ...cached.response,
      notifications: cached.response.notifications?.map((n) =>
        n.id === id ? { ...n, ...patch } : n
      ),
    },
  };
}

function patchMembersConnection(
  members: Array<{ id?: string; userId?: string; user?: { id?: string } | null }> | undefined,
  peerIds: Set<string>,
  status: ConnectionStatus | null
) {
  if (!members) return members;
  return members.map((member) =>
    directoryEntryMatchesPeers(member, peerIds)
      ? {
          ...member,
          hasConnectionRequest: status != null,
          connectionRequestType: status,
        }
      : member
  );
}

function patchParticipantGroupCache(
  current: unknown,
  peerIds: string[],
  status: ConnectionStatus | null
) {
  const cached = current as {
    participants?: Array<{
      participants?: Array<{ id?: string }>;
      exhibitors?: Array<{ id?: string }>;
      sponsors?: Array<{ id?: string }>;
    }>;
  } | undefined;
  if (!cached?.participants) return current;
  const ids = new Set(peerIds.filter(Boolean));
  return {
    ...cached,
    participants: cached.participants.map((group) => ({
      ...group,
      participants: patchMembersConnection(group.participants, ids, status),
      exhibitors: patchMembersConnection(group.exhibitors, ids, status),
      sponsors: patchMembersConnection(group.sponsors, ids, status),
    })),
  };
}

function bumpLiveInboxCache(
  cached: InboxListResponse | undefined,
  incoming: InboxNotification
): InboxListResponse | undefined {
  if (!cached?.response) return cached;
  const items = cached.response.notifications ?? [];
  if (items.some((n) => n.id === incoming.id)) return cached;
  const page = cached.response.pagination?.currentPage ?? 1;
  return {
    ...cached,
    response: {
      ...cached.response,
      notifications: page === 1 ? [incoming, ...items] : items,
      pagination: {
        ...cached.response.pagination,
        totalUnread: (cached.response.pagination?.totalUnread ?? 0) + 1,
        total: (cached.response.pagination?.total ?? 0) + 1,
      },
    },
  };
}

function markAllAsReadInCache(
  cached: InboxListResponse | undefined,
  readAt: Date
): InboxListResponse | undefined {
  if (!cached?.response) return cached;
  const readAtIso = readAt.toISOString();
  return {
    ...cached,
    response: {
      ...cached.response,
      notifications: cached.response.notifications?.map((n) =>
        n.readAt ? n : { ...n, readAt: readAtIso }
      ),
      pagination: {
        ...cached.response.pagination,
        totalUnread: 0,
      },
    },
  };
}

function notificationCacheKeyMatcher(slug: string | undefined) {
  return (key: unknown) =>
    typeof key === "string" &&
    (key.includes("/api/notification?") ||
      (!!slug && key.includes(`/api/event/${slug}/notifications`)));
}

type Translate = (key: string, options?: Record<string, string>) => string;

type PartyInfo = {
  id: string;
  name: string;
  image: string | null;
  type: PartyKind;
};

function revalidateAgenda(slug: string | undefined) {
  if (!slug) return;
  void globalMutate(
    (key) =>
      typeof key === "string" && key.includes(`/api/event/${slug}/my-agenda`),
    undefined,
    { revalidate: true }
  );
}

function applyConnectionStatusLocally(
  slug: string | undefined,
  peerIds: Array<string | null | undefined>,
  status: ConnectionStatus | null,
  pair?: { senderId?: string | null; receiverId?: string | null }
) {
  const ids = peerIds.filter((id): id is string => Boolean(id));
  emitLiveConnectionStatus(ids, status, pair);
  if (!slug || ids.length === 0) return;
  void globalMutate(
    (key) =>
      typeof key === "string" &&
      key.includes(`/api/event/${slug}/participant-group`),
    (current) => patchParticipantGroupCache(current, ids, status),
    { revalidate: false }
  );
}

const getTransactionsAlertTitleAndContent = (
  alert:
    | { feature?: TransactionsAlertFeature; type?: TransactionsAlertType }
    | null
    | undefined,
  t: (key: string) => string
) => {
  const feature = alert?.feature;
  const type = alert?.type;

  const featureLabel = (() => {
    switch (feature) {
      case TransactionsAlertFeature.EVENT:
        return t("events");
      case TransactionsAlertFeature.ATTENDEE:
        return t("attendees");
      case TransactionsAlertFeature.FLOOR_PLAN:
        return t("floor plans");
      case TransactionsAlertFeature.SEATING:
        return t("seating");
      case TransactionsAlertFeature.BOOTH:
        return t("booths");
      case TransactionsAlertFeature.EMAIL:
        return t("emails");
      case TransactionsAlertFeature.SMS:
        return t("SMS");
      case TransactionsAlertFeature.WHATSAPP:
        return t("WhatsApp");
      case TransactionsAlertFeature.TEAM:
        return t("team members");
      case TransactionsAlertFeature.SUBSCRIPTION:
        return t("subscription");
      default:
        return t("subscription");
    }
  })();

  if (feature === TransactionsAlertFeature.SUBSCRIPTION) {
    switch (type) {
      case TransactionsAlertType.SUBSCRIBED:
        return {
          title: t("Subscription Created"),
          content: t(
            "Your subscription has been created successfully. You can now start using Sajilni."
          ),
        };
      case TransactionsAlertType.RENEWED:
        return {
          title: t("Subscription Renewed"),
          content: t("Your subscription has been renewed successfully."),
        };
      case TransactionsAlertType.CANCELLED:
        return {
          title: t("Subscription Cancelled"),
          content: t("Your subscription has been cancelled."),
        };
      case TransactionsAlertType.FAILED:
        return {
          title: t("Subscription Failed"),
          content: t("Your subscription payment has failed."),
        };
      default:
        return {
          title: t("Subscription Alert"),
          content: t("You have a subscription alert."),
        };
    }
  }

  switch (type) {
    case TransactionsAlertType.EXPIRING:
      return {
        title: t(`${featureLabel} quota almost reached`),
        content: t(
          `You have used more than 80% of your ${featureLabel} quota.`
        ),
      };
    case TransactionsAlertType.EXPIRED:
      return {
        title: t(`${featureLabel} quota reached`),
        content: t(`You have reached 100% of your ${featureLabel} quota.`),
      };
    default:
      return {
        title: t(`${featureLabel} quota alert`),
        content: t(`You have a quota alert for your ${featureLabel}.`),
      };
  }
};

export const getSenderInfo = (notification: InboxNotification): PartyInfo => {
  if (notification?.type === NotificationsType.TRANSACTIONS_ALERT) {
    return { id: "", name: "Sajilni", image: null, type: "user" };
  }

  if (
    notification.sender === ParticipantType.EXHIBITOR &&
    notification.senderExhibitor
  ) {
    const { name, image } = displayCompanyName(notification.senderExhibitor);
    return {
      id: notification.senderExhibitor.id,
      name: name || "Unknown",
      image,
      type: "business",
    };
  }
  if (
    notification.sender === ParticipantType.SPONSOR &&
    notification.senderSponsor
  ) {
    const { name, image } = displayCompanyName(notification.senderSponsor);
    return {
      id: notification.senderSponsor.id,
      name: name || "Unknown",
      image,
      type: "business",
    };
  }
  if (notification.senderUser) {
    const { name, image } = displayPersonName(notification.senderUser);
    return {
      id: notification.senderUser.id,
      name: name || "Unknown",
      image,
      type: "user",
    };
  }
  if (notification.senderExhibitor) {
    const { name, image } = displayCompanyName(notification.senderExhibitor);
    return {
      id: notification.senderExhibitor.id,
      name: name || "Unknown",
      image,
      type: "business",
    };
  }
  if (notification.senderSponsor) {
    const { name, image } = displayCompanyName(notification.senderSponsor);
    return {
      id: notification.senderSponsor.id,
      name: name || "Unknown",
      image,
      type: "business",
    };
  }
  return { id: "", name: "Unknown", image: null, type: "user" };
};

export const getReceiverInfo = (notification: InboxNotification): PartyInfo => {
  if (
    notification.receiver === ParticipantType.EXHIBITOR &&
    notification.receiverExhibitor
  ) {
    const { name, image } = displayCompanyName(notification.receiverExhibitor);
    return {
      id: notification.receiverExhibitor.id,
      name: name || "Unknown",
      image,
      type: "business",
    };
  }
  if (
    notification.receiver === ParticipantType.SPONSOR &&
    notification.receiverSponsor
  ) {
    const { name, image } = displayCompanyName(notification.receiverSponsor);
    return {
      id: notification.receiverSponsor.id,
      name: name || "Unknown",
      image,
      type: "business",
    };
  }
  if (notification.receiverUser) {
    const { name, image } = displayPersonName(notification.receiverUser);
    return {
      id: notification.receiverUser.id,
      name: name || "Unknown",
      image,
      type: "user",
    };
  }
  if (notification.receiverExhibitor) {
    const { name, image } = displayCompanyName(notification.receiverExhibitor);
    return {
      id: notification.receiverExhibitor.id,
      name: name || "Unknown",
      image,
      type: "business",
    };
  }
  if (notification.receiverSponsor) {
    const { name, image } = displayCompanyName(notification.receiverSponsor);
    return {
      id: notification.receiverSponsor.id,
      name: name || "Unknown",
      image,
      type: "business",
    };
  }
  return { id: "", name: "Unknown", image: null, type: "user" };
};

function getNotificationContent(
  notification: InboxNotification,
  t: Translate
) {
  const sender = getSenderInfo(notification);
  const otherPartyName = sender.name || t("Unknown");
  const isBusinessReceiver =
    notification.receiver === ParticipantType.EXHIBITOR ||
    notification.receiver === ParticipantType.SPONSOR;
  const inviteeConfirmation = isInviteeSideConfirmation(notification);

  switch (notification.type) {
    case NotificationsType.NEW_CONNECTION_REQUEST:
      return {
        title: t("New Connection Request"),
        content: isBusinessReceiver
          ? t("wants to connect with your Business Profile")
          : t("wants to connect with you"),
        icon: <Users2 className="text-primary h-4 w-4" />,
        showActions: true,
        actionType: "connection",
      };
    case NotificationsType.APPROVED_CONNECTION_REQUEST:
      return inviteeConfirmation
        ? {
            title: t("Connection Request Accepted"),
            content: t("You accepted this connection with {{name}}", {
              name: otherPartyName,
            }),
            icon: <CheckCircle className="h-4 w-4 text-green-500" />,
            showActions: false,
            actionType: "by-me",
          }
        : {
            title: t("Connection Request Accepted"),
            content: t("accepted your connection request"),
            icon: <CheckCircle className="h-4 w-4 text-green-500" />,
            showActions: false,
          };
    case NotificationsType.REJECTED_CONNECTION_REQUEST:
      return inviteeConfirmation
        ? {
            title: t("Connection Request Rejected"),
            content: t("You rejected this connection with {{name}}", {
              name: otherPartyName,
            }),
            icon: <XCircle className="h-4 w-4 text-red-500" />,
            showActions: false,
            actionType: "by-me",
          }
        : {
            title: t("Connection Request Rejected"),
            content: t("rejected your connection request"),
            icon: <XCircle className="h-4 w-4 text-red-500" />,
            showActions: false,
          };
    case NotificationsType.NEW_MESSAGE:
      return {
        title: t("New Message"),
        content: isBusinessReceiver
          ? t("sent a new message on your Business Profile")
          : t("sent you a message"),
        icon: <MessageCircle className="text-primary h-4 w-4" />,
        showActions: false,
      };
    case NotificationsType.SEND_MEETING_REQUEST:
      return {
        title: t("Meeting Request Sent"),
        content: isBusinessReceiver
          ? t("has requested a meeting with your Business Profile")
          : t("has requested a meeting with you"),
        icon: <Calendar className="text-primary h-4 w-4" />,
        showActions: true,
        meeting: notification.meeting,
        actionType: "meeting",
      };
    case NotificationsType.APPROVED_MEETING_REQUEST:
      return inviteeConfirmation
        ? {
            title: t("Meeting Request Accepted"),
            content: t("You accepted this meeting with {{name}}", {
              name: otherPartyName,
            }),
            icon: <Calendar className="h-4 w-4 text-green-500" />,
            showActions: false,
            meeting: notification.meeting,
            actionType: "by-me",
          }
        : {
            title: t("Meeting Request Accepted"),
            content: t("accepted your meeting request"),
            icon: <Calendar className="h-4 w-4 text-green-500" />,
            showActions: false,
            meeting: notification.meeting,
          };
    case NotificationsType.REJECTED_MEETING_REQUEST:
      return inviteeConfirmation
        ? {
            title: t("Meeting Request Rejected"),
            content: t("You rejected this meeting with {{name}}", {
              name: otherPartyName,
            }),
            icon: <Calendar className="h-4 w-4 text-red-500" />,
            showActions: false,
            meeting: notification.meeting,
            actionType: "by-me",
          }
        : {
            title: t("Meeting Request Rejected"),
            content: t("rejected your meeting request"),
            icon: <Calendar className="h-4 w-4 text-red-500" />,
            showActions: false,
            meeting: notification.meeting,
          };
    case NotificationsType.COMMUNICATION:
      return {
        title: notification.title || t("Notification"),
        content: notification.body || "",
        icon: <BellRing className="text-primary h-4 w-4" />,
        showActions: false,
      };
    case NotificationsType.TRANSACTIONS_ALERT: {
      const transactionsAlert =
        notification?.transactionsAlerts?.[0] ??
        notification?.transactionsAlert ??
        null;
      const { title, content } = getTransactionsAlertTitleAndContent(
        transactionsAlert,
        t
      );
      return {
        title,
        content,
        icon: <AlertCircle className="h-4 w-4 text-red-500" />,
        showActions: false,
        transactionsAlert,
        actionType: "transactions-alert",
      };
    }
    default:
      return {
        title: t("Notification"),
        content: t("You have a new notification"),
        icon: <BellRing className="text-primary h-4 w-4" />,
        showActions: false,
      };
  }
}

/** `active` gates local pagination (popover open). SWR always runs for the unread badge. */
export function useNotificationInbox(active: boolean) {
  const { t } = useTranslation("notification");
  const locale = useLocale();
  const { slug } = useParams() as { slug?: string };

  const [currentPage, setCurrentPage] = useState(1);
  const [allNotifications, setAllNotifications] = useState<InboxNotification[]>(
    []
  );
  const [totalCount, setTotalCount] = useState(0);
  const [pendingActionKeys, setPendingActionKeys] = useState(
    () => new Set<string>()
  );
  const liveNotificationIds = useRef(new Set<string>());

  const { isOrganizer, organizationId } = useSassTrackStore();

  const notificationsUrl =
    isOrganizer && organizationId
      ? `/api/notification?organizationId=${organizationId}&page=${currentPage}&limit=10`
      : slug
        ? `/api/event/${slug}/notifications?page=${currentPage}&limit=10`
        : null;

  const { data, isLoading, isValidating, mutate } = useSWRFetcher(
    notificationsUrl,
    baseApi,
    { revalidateOnFocus: false }
  );
  const inboxData = data as InboxListResponse | undefined;
  const cachedPageNotifications = useMemo(
    () =>
      inboxData?.response &&
      (inboxData.response.pagination?.currentPage ?? 1) === currentPage
        ? inboxData.response.notifications ?? []
        : [],
    [inboxData, currentPage]
  );
  const displayNotifications =
    allNotifications.length > 0 ? allNotifications : cachedPageNotifications;
  const effectiveTotalCount =
    totalCount || inboxData?.response?.pagination?.total || 0;
  const isInitialLoading =
    isLoading && currentPage === 1 && displayNotifications.length === 0;
  const isLoadingMore = currentPage > 1 && (isLoading || isValidating);
  const hasNextPage =
    effectiveTotalCount > 0 &&
    displayNotifications.length < effectiveTotalCount;

  const { trigger: markAllAsReadTrigger } = useSWRMutationHook(
    isOrganizer && organizationId
      ? `/api/notification?organizationId=${organizationId}`
      : slug
        ? `/api/event/${slug}/notifications`
        : "",
    baseApi,
    "put"
  );

  const { trigger: respondToConnectionRequest } = useSWRMutationHook(
    `/api/event/${slug}/connection-request`,
    baseApi,
    "put"
  );

  const { trigger: respondToMeetingRequest } = useSWRMutationHook(
    `/api/event/${slug}/meetings`,
    baseApi,
    "put"
  );

  const markActionPending = (key: string) => {
    setPendingActionKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };
  const clearActionPending = (key: string) => {
    setPendingActionKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const isActionPending = (
    notificationId: string,
    action: "accept" | "reject"
  ) => pendingActionKeys.has(`${notificationId}:${action}`);
  const isNotificationPending = (notificationId: string) =>
    pendingActionKeys.has(`${notificationId}:accept`) ||
    pendingActionKeys.has(`${notificationId}:reject`);

  useEffect(() => {
    if (active) return;
    setCurrentPage(1);
    setAllNotifications([]);
    setTotalCount(0);
  }, [active]);

  useLayoutEffect(() => {
    if (!active || !inboxData?.response) return;
    const pageItems = inboxData.response.notifications || [];
    const pagination = inboxData.response.pagination;
    const responsePage = pagination?.currentPage ?? 1;
    const total = pagination?.total ?? 0;

    if (responsePage !== currentPage) return;

    if (currentPage === 1) {
      setAllNotifications((prev) => {
        const ids = new Set(pageItems.map((n) => n.id));
        const liveOnly = prev.filter(
          (n) => !ids.has(n.id) && liveNotificationIds.current.has(n.id)
        );
        const tail = prev.filter(
          (n) => !ids.has(n.id) && !liveNotificationIds.current.has(n.id)
        );
        for (const id of ids) liveNotificationIds.current.delete(id);
        return [...liveOnly, ...pageItems, ...tail];
      });
      setTotalCount(total);
    } else {
      setAllNotifications((prev) => {
        const incoming = pageItems.filter(
          (n) => !prev.some((existing) => existing.id === n.id)
        );
        return [...prev, ...incoming];
      });
      setTotalCount(total);
    }
  }, [inboxData, currentPage, active]);

  const setNotificationItems = useCallback(
    (updater: SetStateAction<InboxNotification[]>) => {
      setAllNotifications((prev) => {
        const base = prev.length > 0 ? prev : cachedPageNotifications;
        if (typeof updater === "function") {
          return (updater as (items: InboxNotification[]) => InboxNotification[])([
            ...base,
          ]);
        }
        return updater;
      });
    },
    [cachedPageNotifications]
  );

  const loadMoreNotifications = useCallback(() => {
    if (!hasNextPage || isLoadingMore || isLoading) return;
    setCurrentPage((page) => page + 1);
  }, [hasNextPage, isLoadingMore, isLoading]);

  const handleNotificationsScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < 80) loadMoreNotifications();
    },
    [loadMoreNotifications]
  );

  const unreadCount = inboxData?.response?.pagination?.totalUnread || 0;
  const hasUnreadMeetings = displayNotifications.some(
    (n) =>
      (n.type === NotificationsType.RECEIVED_MEETING_REQUEST ||
        n.type === NotificationsType.APPROVED_MEETING_REQUEST) &&
      !n.readAt
  );

  const applyLiveNotification = useCallback(
    (item: { id: string } & Record<string, unknown>) => {
      if (!item.id) return;
      const incoming = item as InboxNotification;

      if (active) {
        liveNotificationIds.current.add(incoming.id);
        setNotificationItems((prev) => {
          if (prev.some((n) => n.id === incoming.id)) return prev;
          return [incoming, ...prev];
        });
      }

      if (
        incoming.type === NotificationsType.NEW_CONNECTION_REQUEST ||
        incoming.type === NotificationsType.APPROVED_CONNECTION_REQUEST ||
        incoming.type === NotificationsType.REJECTED_CONNECTION_REQUEST
      ) {
        applyConnectionStatusLocally(
          slug,
          connectionNotificationPeerIds(incoming),
          incoming.type === NotificationsType.APPROVED_CONNECTION_REQUEST
            ? ConnectionStatus.ACCEPTED
            : incoming.type === NotificationsType.NEW_CONNECTION_REQUEST
              ? ConnectionStatus.PENDING
              : null,
          {
            senderId:
              incoming.connectionRequest?.senderId ?? incoming.senderId,
            receiverId:
              incoming.connectionRequest?.receiverId ?? incoming.receiverId,
          }
        );
      }

      void globalMutate(
        notificationCacheKeyMatcher(slug),
        (current) =>
          bumpLiveInboxCache(current as InboxListResponse | undefined, incoming),
        { revalidate: false }
      );
    },
    [active, setNotificationItems, slug]
  );

  const markAllAsRead = () =>
    markAllAsReadTrigger().then(() => {
      const readAt = new Date();
      const markRead = (items: InboxNotification[]) =>
        items.map((n) => (n.readAt ? n : { ...n, readAt }));

      setNotificationItems((prev) => markRead(prev));
      void mutate(
        (current) =>
          markAllAsReadInCache(current as InboxListResponse | undefined, readAt),
        { revalidate: false }
      );
      void globalMutate(
        notificationCacheKeyMatcher(slug),
        (current) =>
          markAllAsReadInCache(current as InboxListResponse | undefined, readAt),
        { revalidate: false }
      );
    });

  return {
    slug,
    t,
    locale,
    allNotifications: displayNotifications,
    setAllNotifications: setNotificationItems,
    currentPage,
    totalCount: effectiveTotalCount,
    unreadCount,
    hasUnreadMeetings,
    isLoading,
    isInitialLoading,
    isLoadingMore,
    hasNextPage,
    handleNotificationsScroll,
    loadMoreNotifications,
    markAllAsRead,
    applyLiveNotification,
    mutate,
    respondToConnectionRequest,
    respondToMeetingRequest,
    markActionPending,
    clearActionPending,
    isActionPending,
    isNotificationPending,
  };
}

type Inbox = ReturnType<typeof useNotificationInbox>;

export function NotificationList({
  inbox,
  variant,
}: {
  inbox: Inbox;
  variant: "popover" | "page";
}) {
  const {
    slug,
    t,
    locale,
    allNotifications,
    setAllNotifications,
    isInitialLoading,
    isLoadingMore,
    hasNextPage,
    handleNotificationsScroll,
    loadMoreNotifications,
    mutate,
    respondToConnectionRequest,
    respondToMeetingRequest,
    markActionPending,
    clearActionPending,
    isActionPending,
    isNotificationPending,
  } = inbox;

  const formatTime = (timestamp: Date | string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), {
        addSuffix: true,
        locale: locale === "ar" ? ar : undefined,
      });
    } catch {
      return t("recently");
    }
  };

  return (
    <div
      onScroll={handleNotificationsScroll}
      className={cn(
        "overflow-y-auto overscroll-contain",
        variant === "page"
          ? "max-h-[min(75vh,42rem)]"
          : "max-h-[min(70vh,28rem)]"
      )}
    >
      {isInitialLoading ? (
        <div className="space-y-3 p-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-2.5">
              <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : allNotifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
          <BellRing className="text-muted-foreground mb-2 h-8 w-8" />
          <p className="text-muted-foreground text-sm">
            {t("No notifications yet")}
          </p>
        </div>
      ) : (
        <div>
          {allNotifications.map((notification) => {
            const sender = getSenderInfo(notification);
            const receiver = getReceiverInfo(notification);
            const { title, content, icon, showActions, meeting, actionType } =
              getNotificationContent(notification, t);

            const isBusinessProfile = receiver.type === "business";
            const isUnread = !notification.readAt;
            const isOwnAction = actionType?.includes("by-me");
            const hideSenderPrefix =
              isOwnAction ||
              notification.type === NotificationsType.COMMUNICATION;
            const isAccepted =
              notification.type ===
                NotificationsType.APPROVED_CONNECTION_REQUEST ||
              notification.type === NotificationsType.APPROVED_MEETING_REQUEST;
            const isRejected =
              notification.type ===
                NotificationsType.REJECTED_CONNECTION_REQUEST ||
              notification.type === NotificationsType.REJECTED_MEETING_REQUEST;

            return (
              <div
                key={notification.id}
                className={cn(
                  "hover:bg-muted/50 border-b border-s-2 border-s-transparent px-3 py-2.5 transition-colors last:border-b-0 sm:px-4",
                  isUnread && "border-s-primary bg-muted/70"
                )}
              >
                <div className="flex items-start gap-2.5">
                  <div className="relative shrink-0">
                    <Avatar
                      className={cn(
                        "h-9 w-9",
                        isAccepted && "border-2 border-green-500",
                        isRejected && "border-2 border-red-500"
                      )}
                    >
                      <AvatarImage
                        src={sender.image || "/placeholder.svg"}
                        alt={sender.name}
                      />
                      <AvatarFallback
                        className={cn(
                          isAccepted && "bg-green-100",
                          isRejected && "bg-red-100"
                        )}
                      >
                        {sender.type === "business" ? (
                          <Building2 className="h-4 w-4" />
                        ) : (
                          <User className="h-4 w-4" />
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div
                      className={cn(
                        "bg-background absolute -bottom-0.5 -end-0.5 rounded-full p-0.5",
                        isAccepted && "bg-green-100",
                        isRejected && "bg-red-100"
                      )}
                    >
                      {icon}
                    </div>
                  </div>

                  <div className="min-w-0 flex-1 text-start">
                    <p
                      className={cn(
                        "text-sm leading-snug",
                        isUnread ? "font-semibold" : "font-medium"
                      )}
                    >
                      {title}
                    </p>

                    <p className="text-muted-foreground mt-0.5 text-xs leading-snug">
                      {hideSenderPrefix ? (
                        <span>{content}</span>
                      ) : (
                        <>
                          <span className="text-foreground font-medium">
                            {sender.name}
                          </span>{" "}
                          {content}
                        </>
                      )}
                      {isBusinessProfile && !isOwnAction ? (
                        <>
                          {" "}
                          <span className="text-muted-foreground inline-flex items-center text-[11px]">
                            <Building2 className="me-1 h-3 w-3" />
                            {receiver.name}
                          </span>
                        </>
                      ) : null}
                    </p>

                    {notification.meeting?.startDate ? (
                      <p className="text-muted-foreground mt-1 break-words text-[11px] leading-snug">
                        {t("meetingDate")}:{" "}
                        {`${moment
                          .tz(
                            notification.meeting.startDate,
                            notification.event?.timezone ?? "UTC"
                          )
                          .format("YYYY-MM-DD HH:mm")} - ${moment
                          .tz(
                            notification.meeting.endDate,
                            notification.event?.timezone ?? "UTC"
                          )
                          .format("HH:mm")} on ${
                          notification.event?.timezone ?? "UTC"
                        } timezone`}
                      </p>
                    ) : null}

                    <div className="text-muted-foreground mt-1 flex items-center gap-1 text-[11px] leading-5">
                      <Clock className="h-3 w-3 shrink-0 rtl:-scale-x-100" />
                      <span>{formatTime(notification.sentAt)}</span>
                    </div>
                    {showActions && actionType === "connection" && (
                      <div className="mt-2 flex gap-2">
                        <Button
                          buttonColor="primary"
                          size="sm"
                          disabled={isNotificationPending(notification.id)}
                          onClick={() => {
                            const key = `${notification.id}:accept`;
                            markActionPending(key);
                            respondToConnectionRequest({
                              id: notification.entityId,
                              status: ConnectionStatus.ACCEPTED,
                              notificationId: notification.id,
                            })
                              .then(() => {
                                const patch = {
                                  ...notification,
                                  type: NotificationsType.APPROVED_CONNECTION_REQUEST,
                                  readAt: new Date(),
                                  sentAt: new Date(),
                                  connectionRequest:
                                    notification.connectionRequest ?? {
                                      senderId: notification.senderId,
                                      receiverId: notification.receiverId,
                                    },
                                };
                                setAllNotifications((prev) => {
                                  prev.splice(
                                    prev.findIndex(
                                      (n) => n.id === notification.id
                                    ),
                                    1,
                                    patch
                                  );
                                  return [...prev];
                                });
                                void mutate(
                                  (current) =>
                                    patchNotificationInCache(
                                      current as InboxListResponse | undefined,
                                      notification.id,
                                      patch
                                    ),
                                  { revalidate: false }
                                );
                                applyConnectionStatusLocally(
                                  slug,
                                  connectionNotificationPeerIds(notification),
                                  ConnectionStatus.ACCEPTED,
                                  {
                                    senderId:
                                      notification.connectionRequest?.senderId ??
                                      notification.senderId,
                                    receiverId:
                                      notification.connectionRequest
                                        ?.receiverId ?? notification.receiverId,
                                  }
                                );
                              })
                              .finally(() => clearActionPending(key));
                          }}
                          className="h-8 w-full text-xs"
                        >
                          {isActionPending(notification.id, "accept") ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            t("Accept")
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outlined"
                          disabled={isNotificationPending(notification.id)}
                          onClick={() => {
                            const key = `${notification.id}:reject`;
                            markActionPending(key);
                            respondToConnectionRequest({
                              id: notification.entityId,
                              status: ConnectionStatus.REJECTED,
                              notificationId: notification.id,
                            })
                              .then(() => {
                                const patch = {
                                  ...notification,
                                  type: NotificationsType.REJECTED_CONNECTION_REQUEST,
                                  readAt: new Date(),
                                  sentAt: new Date(),
                                  connectionRequest:
                                    notification.connectionRequest ?? {
                                      senderId: notification.senderId,
                                      receiverId: notification.receiverId,
                                    },
                                };
                                setAllNotifications((prev) => {
                                  prev.splice(
                                    prev.findIndex(
                                      (n) => n.id === notification.id
                                    ),
                                    1,
                                    patch
                                  );
                                  return [...prev];
                                });
                                void mutate(
                                  (current) =>
                                    patchNotificationInCache(
                                      current as InboxListResponse | undefined,
                                      notification.id,
                                      patch
                                    ),
                                  { revalidate: false }
                                );
                                applyConnectionStatusLocally(
                                  slug,
                                  connectionNotificationPeerIds(notification),
                                  null,
                                  {
                                    senderId:
                                      notification.connectionRequest?.senderId ??
                                      notification.senderId,
                                    receiverId:
                                      notification.connectionRequest
                                        ?.receiverId ?? notification.receiverId,
                                  }
                                );
                              })
                              .finally(() => clearActionPending(key));
                          }}
                          className="h-8 w-full text-xs"
                        >
                          {isActionPending(notification.id, "reject") ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            t("Reject")
                          )}
                        </Button>
                      </div>
                    )}

                    {showActions && actionType === "meeting" && meeting && (
                      <div className="mt-2 flex gap-2">
                        <Button
                          buttonColor="primary"
                          size="sm"
                          disabled={isNotificationPending(notification.id)}
                          onClick={() => {
                            const key = `${notification.id}:accept`;
                            markActionPending(key);
                            respondToMeetingRequest({
                              meetingId: meeting.id,
                              action: MeetingStatus.ACCEPTED,
                              notificationId: notification.id,
                            })
                              .then(() => {
                                setAllNotifications((prev) => {
                                  prev.splice(
                                    prev.findIndex(
                                      (n) => n.id === notification.id
                                    ),
                                    1,
                                    {
                                      ...notification,
                                      type: NotificationsType.APPROVED_MEETING_REQUEST,
                                      readAt: new Date(),
                                      sentAt: new Date(),
                                      meeting: {
                                        ...meeting,
                                        participants: meeting.participants ?? [
                                          {
                                            entityId: notification.senderId,
                                            type: MeetingParticipantType.SENDER,
                                          },
                                          {
                                            entityId: notification.receiverId,
                                            type: MeetingParticipantType.RECEIVER,
                                          },
                                        ],
                                      },
                                    }
                                  );
                                  return [...prev];
                                });
                                void mutate(
                                  (current) =>
                                    patchNotificationInCache(
                                      current as InboxListResponse | undefined,
                                      notification.id,
                                      {
                                        type: NotificationsType.APPROVED_MEETING_REQUEST,
                                        readAt: new Date(),
                                        sentAt: new Date(),
                                      }
                                    ),
                                  { revalidate: false }
                                );
                                revalidateAgenda(slug);
                              })
                              .finally(() => clearActionPending(key));
                          }}
                          className="h-8 w-full text-xs"
                        >
                          {isActionPending(notification.id, "accept") ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            t("Accept")
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outlined"
                          disabled={isNotificationPending(notification.id)}
                          onClick={() => {
                            const key = `${notification.id}:reject`;
                            markActionPending(key);
                            respondToMeetingRequest({
                              meetingId: meeting.id,
                              action: MeetingStatus.REJECTED,
                              notificationId: notification.id,
                            })
                              .then(() => {
                                setAllNotifications((prev) => {
                                  prev.splice(
                                    prev.findIndex(
                                      (n) => n.id === notification.id
                                    ),
                                    1,
                                    {
                                      ...notification,
                                      type: NotificationsType.REJECTED_MEETING_REQUEST,
                                      readAt: new Date(),
                                      sentAt: new Date(),
                                      meeting: {
                                        ...meeting,
                                        participants: meeting.participants ?? [
                                          {
                                            entityId: notification.senderId,
                                            type: MeetingParticipantType.SENDER,
                                          },
                                          {
                                            entityId: notification.receiverId,
                                            type: MeetingParticipantType.RECEIVER,
                                          },
                                        ],
                                      },
                                    }
                                  );
                                  return [...prev];
                                });
                                void mutate(
                                  (current) =>
                                    patchNotificationInCache(
                                      current as InboxListResponse | undefined,
                                      notification.id,
                                      {
                                        type: NotificationsType.REJECTED_MEETING_REQUEST,
                                        readAt: new Date(),
                                        sentAt: new Date(),
                                      }
                                    ),
                                  { revalidate: false }
                                );
                                revalidateAgenda(slug);
                              })
                              .finally(() => clearActionPending(key));
                          }}
                          className="h-8 w-full text-xs"
                        >
                          {isActionPending(notification.id, "reject") ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            t("Decline")
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <div className="border-t border-gray-100 px-3 py-2 text-center">
            {isLoadingMore ? (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="text-primary h-4 w-4 animate-spin" />
                <span className="text-muted-foreground ms-2 text-xs">
                  {t("Loading more...")}
                </span>
              </div>
            ) : hasNextPage ? (
              <Button
                type="button"
                variant="outlined"
                size="sm"
                className="h-8 w-full text-xs"
                onClick={loadMoreNotifications}
              >
                {t("Load more")}
              </Button>
            ) : allNotifications.length > 10 ? (
              <p className="text-muted-foreground py-1 text-xs">
                {t("No more notifications")}
              </p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

/** Full-page inbox: same items as the bell, roomier chrome. */
export function PortalNotificationsInbox() {
  const { data: session } = useSession();
  const inbox = useNotificationInbox(true);
  usePubNubNotifications(session?.user?.id, inbox.applyLiveNotification);
  const {
    t,
    allNotifications,
    totalCount,
    unreadCount,
    hasUnreadMeetings,
    markAllAsRead,
    isInitialLoading,
  } = inbox;

  return (
    <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <p className="text-muted-foreground text-sm tabular-nums">
          {isInitialLoading
            ? null
            : `${allNotifications.length}${totalCount ? ` / ${totalCount}` : ""}`}
        </p>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {hasUnreadMeetings ? (
            <Badge variant="danger" className="text-xs font-normal">
              <Calendar className="me-1 h-3 w-3" />
              {t("Meetings")}
            </Badge>
          ) : null}
          {unreadCount > 0 ? (
            <Badge variant="outlined" className="text-xs font-normal">
              {unreadCount} {t("unread")}
            </Badge>
          ) : null}
          {unreadCount > 0 ? (
            <Button
              type="button"
              variant="outlined"
              size="sm"
              className="h-8 text-xs"
              onClick={() => {
                void markAllAsRead();
              }}
            >
              {t("Mark all as read")}
            </Button>
          ) : null}
        </div>
      </div>
      <NotificationList inbox={inbox} variant="page" />
    </div>
  );
}
